import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Activity, CheckCircle2, Loader2, Lock, RefreshCw, XCircle } from 'lucide-react';
import { api } from '../api/client.js';
import { Button } from '../components/Button.jsx';
import { Card } from '../components/Card.jsx';
import { Banner } from '../components/Banner.jsx';
import { Input, Textarea } from '../components/Field.jsx';
import { Checkbox } from '../components/Checkbox.jsx';

/**
 * Servers → HTTPS (2026-10-13): Let's Encrypt for many domains (with and
 * without www) on one Apache server, every port-80 request → 301 to https.
 * The backend (lib/httpsSetup.js) does the work; this panel collects the
 * domains, shows the read-only preflight, runs the job and shows its
 * INFO / SUCCESS / ERROR log and the per-domain result.
 */

// Same rules as parseDomains() in backend/lib/httpsSetup.js — a preview only,
// the backend parses again and refuses anything this lets through.
const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

function parseDomains(text) {
  const domains = [];
  const invalid = [];
  const duplicates = [];
  const seen = new Set();
  for (const tok of String(text).split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean)) {
    let d = tok.toLowerCase().replace(/^[a-z][a-z0-9+.-]*:\/\//, '').replace(/[/?#].*$/, '').replace(/:\d+$/, '').replace(/\.$/, '');
    if (d.startsWith('www.')) d = d.slice(4);
    const labels = d.split('.');
    if (!DOMAIN_RE.test(d) || d.length > 253 || labels.some((l) => l.length > 63) || !/[a-z]/.test(labels[labels.length - 1])) {
      invalid.push(tok);
    } else if (seen.has(d)) {
      duplicates.push(tok);
    } else {
      seen.add(d);
      domains.push(d);
    }
  }
  return { domains, invalid, duplicates };
}

const RESULT = {
  https: { icon: '✅', label: 'HTTPS + 301', tone: 'text-ok' },
  already: { icon: '↺', label: 'already had a certificate', tone: 'text-t2' },
  skipped: { icon: '⏭', label: 'skipped', tone: 'text-warn' },
  failed: { icon: '❌', label: 'failed', tone: 'text-err' },
};

function Check({ ok, warn, label, detail }) {
  const Icon = ok ? CheckCircle2 : warn ? Activity : XCircle;
  const tone = ok ? 'text-ok' : warn ? 'text-warn' : 'text-err';
  return (
    <div className="flex items-start gap-2 text-[13px]">
      <Icon className={`mt-0.5 w-4 h-4 shrink-0 ${tone}`} />
      <div className="min-w-0">
        <span className="text-t1">{label}</span>
        {detail && <span className="text-t3"> — {detail}</span>}
      </div>
    </div>
  );
}

function LogView({ log }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [log]);
  const lines = String(log || '').split('\n').filter(Boolean);
  return (
    <div ref={ref} className="max-h-80 overflow-auto rounded-ctl border border-bd bg-s-1 p-3 font-mono text-[11.5px] leading-[1.55]">
      {lines.length === 0 && <span className="text-t3">starting…</span>}
      {lines.map((l, i) => {
        const tone = /^\S+ ERROR /.test(l) ? 'text-err' : /^\S+ SUCCESS /.test(l) ? 'text-ok' : /== /.test(l) ? 'font-semibold text-t1' : 'text-t2';
        return <div key={i} className={`whitespace-pre-wrap break-words ${tone}`}>{l}</div>;
      })}
    </div>
  );
}

function Results({ results, summary }) {
  if (!results?.length) return null;
  const n = (r) => results.filter((x) => x.result === r).length;
  const failedSteps = (summary?.steps ?? []).filter((s) => !s.ok);
  const ok = summary ? summary.ok : results.every((x) => x.result !== 'failed');
  return (
    <div className="space-y-2">
      <Banner compact kind={ok ? 'success' : 'error'} title={ok ? 'Done' : 'Not everything worked'}>
        {n('https')} HTTPS + 301 · {n('already')} already had a certificate · {n('skipped')} skipped · {n('failed')} failed
        {summary && summary.changed === false && ' · nothing changed on the server'}
        {failedSteps.map((s) => <div key={s.step}>step {s.step} failed: {s.detail}</div>)}
      </Banner>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <tbody>
            {results.map((r) => {
              const k = RESULT[r.result] ?? RESULT.failed;
              return (
                <tr key={r.domain} className="border-b border-bd align-top last:border-0">
                  <td className="w-6 py-1.5 pr-2">{k.icon}</td>
                  <td className="py-1.5 pr-3 font-mono text-[12.5px] text-t1 whitespace-nowrap">{r.domain}</td>
                  <td className={`py-1.5 pr-3 whitespace-nowrap ${k.tone}`}>{k.label}</td>
                  <td className="py-1.5 text-t2 break-words">{r.reason}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {summary?.firewall && (
        <p className="text-[12px] text-t3">
          Firewall: {summary.firewall.kind} — {{
            allowed: 'SSH + Apache Full allowed',
            opened: '80/443 opened in firewalld',
            untouched: 'hand-made ruleset, not touched',
            left_off: 'UFW left off',
            enabled: 'UFW turned on (new SSH connection proved)',
            rolled_back: 'UFW turned on, a new SSH connection failed → turned off again',
            not_enabled: 'a rule failed — UFW not turned on',
            enable_failed: 'ufw enable failed',
          }[summary.firewall.action] ?? summary.firewall.action}
        </p>
      )}
    </div>
  );
}

export function ServerHttpsPanel({ server }) {
  const [info, setInfo] = useState(null);
  const [notice, setNotice] = useState(null);

  const [domainsText, setDomainsText] = useState('');
  const [includeWww, setIncludeWww] = useState(true);
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [docroot, setDocroot] = useState('');
  const [docrootTouched, setDocrootTouched] = useState(false);
  const [enableUfw, setEnableUfw] = useState(false);

  const [pf, setPf] = useState(null);           // { parsed, preflight, domains }
  const [pfFor, setPfFor] = useState(null);
  const [pfLoading, setPfLoading] = useState(false);
  const [pfErr, setPfErr] = useState(null);

  const [jobLog, setJobLog] = useState('');
  const [jobRunning, setJobRunning] = useState(false);
  const [jobOutcome, setJobOutcome] = useState(null);
  const pollRef = useRef(null);

  const parsed = useMemo(() => parseDomains(domainsText), [domainsText]);
  const key = `${parsed.domains.join(',')}|${includeWww}`;

  const load = useCallback(async () => {
    try {
      const d = await api(`/api/servers/${server.id}/https`);
      setInfo(d);
      if (!emailTouched && d.last_email) setEmail(d.last_email);
    } catch (err) {
      setNotice(err.body?.detail ?? err.message);
    }
  }, [server.id, emailTouched]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  async function runPreflight() {
    setPfLoading(true);
    setPfErr(null);
    setPf(null);
    try {
      const r = await api(`/api/servers/${server.id}/https/preflight`, {
        method: 'POST', body: { domains: parsed.domains.join('\n'), include_www: includeWww },
      });
      setPf(r);
      setPfFor(key);
      if (!docrootTouched) setDocroot(r.preflight.apache.default_docroot);
      if (r.preflight.firewall.kind !== 'none') setEnableUfw(false);
    } catch (err) {
      setPfErr(err.body?.detail ?? err.message);
    } finally {
      setPfLoading(false);
    }
  }

  function pollJob(jobId) {
    setJobRunning(true);
    setJobLog('');
    setJobOutcome(null);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const p = await api(`/api/servers/${server.id}/https/jobs/${jobId}`);
        setJobLog(p.log ?? '');
        if (p.job.status === 'done' || p.job.status === 'failed') {
          clearInterval(pollRef.current);
          setJobRunning(false);
          setJobOutcome(p.job.result ?? p.job.error);
          load();
        }
      } catch { /* keep polling */ }
    }, 2000);
  }

  const p = pf?.preflight;
  const pfMatches = pf && pfFor === key;
  const toAttempt = pfMatches ? pf.domains.filter((d) => !d.skip) : [];
  const ufwOffered = p && p.firewall.kind === 'none';
  const sshPort = info?.server?.ssh_port ?? 22;
  const ufwPorts = p ? [
    `${sshPort}/tcp (SSH)`, '80/tcp', '443/tcp',
    ...p.public_ports.tcp.filter((x) => ![sshPort, 80, 443].includes(x.port)).map((x) => `${x.port}/tcp${x.procs.length ? ` (${x.procs.join(',')})` : ''}`),
    ...p.public_ports.udp.map((x) => `${x.port}/udp${x.procs.length ? ` (${x.procs.join(',')})` : ''}`),
  ] : [];
  const missingPkgs = p ? Object.entries(p.packages).filter(([, v]) => !v).map(([k]) => k) : [];
  const canRun = pfMatches && p.can_run && toAttempt.length > 0 && parsed.invalid.length === 0
    && (email === '' || EMAIL_RE.test(email)) && docroot.startsWith('/') && !jobRunning && !info?.busy;

  async function run() {
    const lines = [
      `Run HTTPS on "${server.name}" for ${toAttempt.length} domain(s)?`,
      '',
      ...toAttempt.map((d) => `  ${d.names.join(' + ')}`),
      ...(pf.domains.length > toAttempt.length ? ['', `Skipped (DNS): ${pf.domains.filter((d) => d.skip).map((d) => d.domain).join(', ')}`] : []),
      '',
      missingPkgs.length ? `Installs: ${missingPkgs.join(' ')}` : 'No package to install.',
      p.firewall.kind === 'ufw' ? `UFW: allows ${sshPort}/tcp + Apache Full.`
        : ufwOffered && enableUfw ? `UFW: turned ON after allowing ${ufwPorts.join(', ')}.`
          : p.firewall.kind === 'firewalld' ? 'firewalld: opens 80/443.' : 'Firewall: not changed.',
      'Writes a vhost only for names no enabled site has, then certbot --redirect.',
    ];
    if (!window.confirm(lines.join('\n'))) return;
    try {
      const r = await api(`/api/servers/${server.id}/https/run`, {
        method: 'POST',
        body: { domains: parsed.domains.join('\n'), include_www: includeWww, email, docroot, enable_ufw: ufwOffered && enableUfw },
      });
      pollJob(r.job_id);
    } catch (err) {
      const d = err.body?.detail;
      setNotice(`Run refused: ${Array.isArray(d) ? d.join(' · ') : d ?? err.message}`);
    }
  }

  const last = info?.last_run;
  const showLast = !jobRunning && !jobOutcome && last;

  // max-w: the row's action cluster makes the servers table wider than the
  // window, and this panel sits in a cell spanning that width — keep it in view.
  return (
    <div className="max-w-5xl space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-[13.5px] font-semibold text-t1">
        <Lock className="w-4 h-4 text-ic" />
        HTTPS — {server.name}
        <span className="text-[12px] font-normal text-t3">Let&apos;s Encrypt for many domains (+ www) · HTTP → HTTPS 301 · Apache · Ubuntu 22.04 / 24.04</span>
      </div>

      {notice && <Banner compact kind="error" title="Error">{notice}</Banner>}
      {info?.busy && !jobRunning && <Banner compact kind="note" title="Already running">An HTTPS run is going on this server — wait for it to finish.</Banner>}

      {!jobRunning && (
        <Card className="space-y-3 p-4">
          <Textarea
            label="Domains"
            mono
            rows={4}
            value={domainsText}
            onChange={(e) => setDomainsText(e.target.value)}
            placeholder={'example.com\nexample.net, example.org'}
            hint={`One per line, or separated by commas / spaces. www. is added for you. Up to ${info?.max_domains ?? 30} per run.`}
          />
          {domainsText.trim() && (
            <p className="text-[12.5px] text-t2">
              {parsed.domains.length} domain(s){parsed.domains.length ? `: ${parsed.domains.join(', ')}` : ''}
              {parsed.duplicates.length > 0 && <span className="text-t3"> · duplicates dropped: {parsed.duplicates.join(', ')}</span>}
              {parsed.invalid.length > 0 && <span className="text-err"> · not a domain (remove it to run): {parsed.invalid.join(', ')}</span>}
            </p>
          )}
          <Checkbox checked={includeWww} onChange={(e) => setIncludeWww(e.target.checked)} label="Include www (example.com and www.example.com)" />
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Let's Encrypt e-mail (optional)"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value.trim()); setEmailTouched(true); }}
              hint={info?.last_email && !emailTouched ? 'pre-filled with the last one used' : 'only the Let’s Encrypt account contact — empty = registered without one'}
            />
            <Input
              label="Document root"
              mono
              value={docroot}
              placeholder="/var/www/html"
              onChange={(e) => { setDocroot(e.target.value); setDocrootTouched(true); }}
              hint={p && !docrootTouched
                ? `same as the server's default site${p.apache.default_docroot_from ? ` (${p.apache.default_docroot_from})` : ' (Apache default)'}`
                : 'used only for a domain that has no vhost yet'}
            />
          </div>
          {ufwOffered && (
            <div className="space-y-1">
              <Checkbox
                checked={enableUfw}
                onChange={(e) => setEnableUfw(e.target.checked)}
                label="Turn UFW on (it is off now)"
              />
              <p className="pl-6 text-[12px] text-t3">
                {enableUfw
                  ? `Allowed first, then enabled: ${ufwPorts.join(', ')}. A new SSH connection must work afterwards, or UFW is turned off again.`
                  : 'Left off by default. Ticked, it first allows SSH, 80, 443 and every port listening publicly now.'}
              </p>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              icon={pfLoading ? Loader2 : RefreshCw}
              className={pfLoading ? '[&>svg]:animate-spin' : ''}
              onClick={runPreflight}
              disabled={pfLoading || parsed.domains.length === 0}
            >
              Preflight (read only)
            </Button>
            <Button
              variant="primary"
              icon={Lock}
              onClick={run}
              disabled={!canRun}
              title={!pfMatches ? 'Run the preflight for these domains first' : undefined}
            >
              Run HTTPS
            </Button>
            {pf && !pfMatches && <span className="text-[12px] text-t3">domains changed — preflight again</span>}
          </div>

          {pfErr && <Banner compact kind="error" title="Preflight failed">{pfErr}</Banner>}
          {p && (
            <div className="space-y-1.5">
              <Check ok={p.os.supported} label={`OS ${p.os.pretty ?? `${p.os.id} ${p.os.version}`}`} detail={p.os.supported ? null : 'Ubuntu 22.04 / 24.04 only'} />
              <Check ok={missingPkgs.length === 0} warn label="Packages" detail={missingPkgs.length ? `will install: ${missingPkgs.join(' ')}` : 'all present'} />
              <Check ok warn={p.firewall.kind === 'custom'} label={`Firewall: ${p.firewall.kind}${p.firewall.ufw_installed && !p.firewall.ufw_active ? ' (UFW installed, off)' : ''}`}
                detail={{
                  ufw: `active — ${sshPort}/tcp + Apache Full will be allowed`,
                  firewalld: 'active — 80/443 will be opened',
                  custom: 'hand-made ruleset — NOT touched; 80/443 must already be open',
                  none: enableUfw ? 'UFW will be turned on (see above)' : 'none active — stays that way',
                }[p.firewall.kind]} />
              <Check ok label="Listening publicly" detail={`TCP ${p.public_ports.tcp.map((x) => x.port).join(', ') || 'none'} · UDP ${p.public_ports.udp.map((x) => x.port).join(', ') || 'none'}`} />
              <Check ok={!p.apache.installed || p.apache.configtest_ok} warn={!p.apache.installed}
                label={`Apache: ${p.apache.installed ? `${p.apache.active}, configtest ${p.apache.configtest_ok ? 'OK' : 'FAILS'}` : 'not installed yet'}`}
                detail={`${p.vhosts.length} enabled vhost(s) · ${p.certs.length} Let's Encrypt certificate(s)`} />
              {p.blockers.length > 0 && <Banner compact kind="error" title="Cannot run — nothing would be changed">{p.blockers.join(' · ')}</Banner>}
              {pf.domains.length > 0 && (
                <div className="overflow-x-auto pt-1">
                  <table className="w-full text-[12.5px]">
                    <thead>
                      <tr className="border-b border-bd text-left text-t3">
                        <th className="py-1 pr-3 font-medium">Domain</th>
                        <th className="py-1 pr-3 font-medium">DNS (from the server)</th>
                        <th className="py-1 pr-3 font-medium">vhost *:80</th>
                        <th className="py-1 font-medium">Certificate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pf.domains.map((d) => (
                        <tr key={d.domain} className="border-b border-bd align-top last:border-0">
                          <td className="py-1.5 pr-3 font-mono text-t1 whitespace-nowrap">{d.domain}</td>
                          <td className={`py-1.5 pr-3 ${d.skip ? 'text-warn' : 'text-t2'}`}>
                            {d.skip ? `⏭ ${d.reason.replace(/^skipped — /, '')}` : `✓ ${d.names.join(' + ')}${d.www_note ? ` — ${d.www_note}` : ''}`}
                          </td>
                          <td className="py-1.5 pr-3 font-mono text-[11.5px] text-t3 break-all">{d.vhost80 ?? (d.skip ? '—' : 'none — will be written')}</td>
                          <td className="py-1.5 text-t2 whitespace-nowrap">{d.cert ? `↺ valid until ${d.cert.until}` : d.skip ? '—' : 'will be requested'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {(jobRunning || jobLog) && (
        <Card className="space-y-3 p-4">
          <div className="flex items-center gap-2 text-[13.5px] font-medium text-t1">
            {jobRunning && <Loader2 className="w-4 h-4 animate-spin text-ic" />}
            {jobRunning ? 'Running — certbot can take a minute per domain…' : 'Run finished'}
          </div>
          <LogView log={jobLog} />
          {jobOutcome && <Results results={jobOutcome.results} summary={jobOutcome} />}
          {!jobRunning && (
            <Button size="sm" onClick={() => { setJobLog(''); setJobOutcome(null); setPf(null); }}>Close the log</Button>
          )}
        </Card>
      )}

      {showLast && (
        <Card className="space-y-2 p-4">
          <div className="text-[13.5px] font-medium text-t1">
            Last run — {new Date(`${last.created_at}Z`).toLocaleString()}
            <span className="ml-2 text-[12px] font-normal text-t3">{last.status}{last.status === 'interrupted' ? ' (the panel restarted during the run — check the server)' : ''}</span>
          </div>
          {last.results ? <Results results={last.results} summary={last.summary} /> : <p className="text-[13px] text-t3">No result recorded.</p>}
          {last.log && (
            <details>
              <summary className="cursor-pointer text-[12.5px] text-t2">Log</summary>
              <div className="mt-2"><LogView log={last.log} /></div>
            </details>
          )}
        </Card>
      )}
    </div>
  );
}
